import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, TextField, InputAdornment,
  IconButton, Chip, Tooltip, CircularProgress, Alert, Switch,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Search, UserCog } from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";

import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

function SubAccountsAdmin() {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(0);
  const [rowsPerPage]         = useState(15);
  const [search,  setSearch]  = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [toggling, setToggling] = useState({});

  const fetchSubs = useCallback(() => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams({
      page: page + 1,
      size: rowsPerPage,
      ...(search ? { q: search } : {}),
    });
    axiosInstance.get(`/admin/subaccounts?${params}`)
      .then(r => { setRows(r.data.subaccounts || []); setTotal(r.data.total || 0); })
      .catch(e => setError(e.response?.data?.message || t("failedLoadSubAccounts")))
      .finally(() => setLoading(false));
  }, [page, rowsPerPage, search]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const handleToggle = async (row) => {
    setToggling(prev => ({ ...prev, [row.id]: true }));
    try {
      const res = await axiosInstance.patch(`/admin/subaccounts/${row.id}/toggle`);
      setRows(prev => prev.map(r => r.id === row.id ? { ...r, isActive: res.data.isActive } : r));
    } catch {
      // silently refresh on error
      fetchSubs();
    } finally {
      setToggling(prev => { const n = { ...prev }; delete n[row.id]; return n; });
    }
  };

  const glassSx = { background: "var(--surface-card)", border: "1px solid var(--line-200)", boxShadow: "var(--shadow-card)", borderRadius: "18px" };
  const cellSx  = { fontSize: "0.8125rem", borderColor: "var(--line-200)", py: 1.25 };
  const headSx  = { ...cellSx, fontWeight: 700, color: "text.secondary", background: isLight ? alpha("#000", 0.025) : alpha("#fff", 0.025) };

  const permLabel = (p) => {
    if (!p) return t("none");
    return p.split(",").map(s => s.trim()).filter(Boolean).join(", ") || t("none");
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: "-0.02em", color: "text.primary", mb: 0.5 }}>{t("subAccountsTitle")}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("subAccountsSub")}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Search */}
      <Box sx={{ mb: 2.5 }}>
        <TextField
          placeholder={t("searchSubAccounts")}
          size="small"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search size={15} /></InputAdornment> }}
          sx={{ width: 360, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "0.875rem" } }}
        />
      </Box>

      {/* Table */}
      <Box sx={{ ...glassSx, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>{t("colAgent")}</TableCell>
                <TableCell sx={headSx}>{t("colUsername")}</TableCell>
                <TableCell sx={headSx}>{t("colExtension")}</TableCell>
                <TableCell sx={headSx}>{t("colSection")}</TableCell>
                <TableCell sx={headSx}>{t("colParentAccount")}</TableCell>
                <TableCell sx={headSx}>{t("colPermissions")}</TableCell>
                <TableCell sx={{ ...headSx, textAlign: "center" }}>{t("colActive")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: "text.secondary", fontSize: "0.875rem" }}>{t("noSubAccountsFound")}</TableCell></TableRow>
              ) : rows.map(row => (
                <TableRow key={row.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell sx={cellSx}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: "8px", background: alpha("#F59E0B", 0.1), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <UserCog size={13} color="#F59E0B" />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.2 }}>
                          {[row.firstName, row.lastName].filter(Boolean).join(" ") || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>{row.email || "—"}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>{row.username || "—"}</TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }}>
                    {row.extension ? (
                      <Chip label={row.extension} size="small" sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600, background: alpha("#6366F1", 0.08), color: "#6366F1", border: "none" }} />
                    ) : "—"}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: "text.secondary" }}>{row.sectionName || "—"}</TableCell>
                  <TableCell sx={cellSx}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", lineHeight: 1.2 }}>{row.parentName || "—"}</Typography>
                      <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>{row.parentEmail || ""}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: "text.secondary", fontSize: "0.75rem" }}>
                    {permLabel(row.permissions)}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, textAlign: "center" }}>
                    {toggling[row.id] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Tooltip title={row.isActive ? t("tooltipDeactivate") : t("tooltipActivate")}>
                        <Switch
                          size="small"
                          checked={!!row.isActive}
                          onChange={() => handleToggle(row)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#22C55E" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#22C55E" },
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[15]}
          onPageChange={(_, p) => setPage(p)}
          sx={{ borderTop: "1px solid var(--line-200)", "& .MuiTablePagination-displayedRows": { fontSize: "0.8125rem" } }}
        />
      </Box>
    </Box>
  );
}

export default SubAccountsAdmin;
